/**
 * Route distance helpers.
 * Prefer Google Maps driving distance (matches Google Maps routing).
 * Falls back to Haversine straight-line only when the Maps API is unavailable.
 */

export function calculateHaversineDistance(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

export async function calculateDrivingDistance(originLat, originLng, destLat, destLng) {
  try {
    if (window.google?.maps?.DistanceMatrixService) {
      const service = new window.google.maps.DistanceMatrixService();
      return new Promise((resolve) => {
        service.getDistanceMatrix(
          {
            origins: [{ lat: originLat, lng: originLng }],
            destinations: [{ lat: destLat, lng: destLng }],
            travelMode: window.google.maps.TravelMode?.DRIVING || 'DRIVING',
            unitSystem: window.google.maps.UnitSystem.METRIC,
          },
          (response, status) => {
            const element = response?.rows?.[0]?.elements?.[0];
            if (status === 'OK' && element?.status === 'OK' && element.distance?.value) {
              resolve(element.distance.value / 1000);
              return;
            }
            console.warn('Distance Matrix failed, using Haversine fallback:', status);
            resolve(calculateHaversineDistance(originLat, originLng, destLat, destLng));
          }
        );
      });
    }
  } catch (error) {
    console.warn('Driving distance error, using Haversine fallback:', error);
  }
  return calculateHaversineDistance(originLat, originLng, destLat, destLng);
}

function hasCoord(value) {
  return value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value));
}

/**
 * Total route km for delivery / bc_service / tradein using driving distance per leg.
 */
export async function computeRouteDistanceKm(route) {
  if (
    !hasCoord(route?.pickup_lat) ||
    !hasCoord(route?.pickup_lng) ||
    !hasCoord(route?.delivery_lat) ||
    !hasCoord(route?.delivery_lng)
  ) {
    return 0;
  }

  const plat = Number(route.pickup_lat);
  const plng = Number(route.pickup_lng);
  const dlat = Number(route.delivery_lat);
  const dlng = Number(route.delivery_lng);

  let total = await calculateDrivingDistance(plat, plng, dlat, dlng);
  const serviceType = route.service_type || 'delivery';

  if (serviceType === 'bc_service') {
    if (!hasCoord(route.point_c_lat) || !hasCoord(route.point_c_lng)) {
      return 0;
    }
    total += await calculateDrivingDistance(
      dlat,
      dlng,
      Number(route.point_c_lat),
      Number(route.point_c_lng)
    );
  } else if (serviceType === 'tradein') {
    total += await calculateDrivingDistance(dlat, dlng, plat, plng);
  }

  return Math.round(total * 10) / 10;
}
